/* eslint-disable max-len */
import { ChangeDetectionStrategy,Component, inject } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { combineLatest, defer, merge } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UserService } from '../shared/user.service';
import { UserInfo } from './UserInfo';
import { toSignal } from '@angular/core/rxjs-interop';


@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'tcc-user-status-signatures',
    template: `
@if (vm$ | async; as vm) {
  <div class="text-end">
    <div>{{ vm.user.name }}</div>
    <div class="small">
      @if (vm.authAction === 'none') {
        @if (vm.user.hasAccessToken) {
          <button
          class="bg-transparent border-0 p-0 m-0 link-light text-decoration-underline small cursor-pointer" (click)="logout()">SIGN OUT</button>
        } @else {
          <button class="bg-transparent border-0 p-0 m-0 link-light text-decoration-underline small cursor-pointer" (click)="login()">SIGN IN</button>
        }
      } @else {
        @if (vm.authAction === 'login') {
          Logging In <i class="fa fa-spinner fa-pulse"></i>
        }
        @if (vm.authAction === 'logout') {
          Logging Out <i class="fa fa-spinner fa-pulse"></i>
        }
      }
    </div>
  </div>
}
`,
    standalone: false
})
export class UserStatusComponent {

  readonly vm$ = defer(() => combineLatest([ this.authAction$, this.userInfo$ ])).pipe(
    map(([authAction, user]) => ({ authAction, user }))
  );

  private readonly authAction$ = this.msalBroadcastSvc.inProgress$.pipe(
    map(x => {
      switch (x) {
        case InteractionStatus.Login: return 'login' as const;
        case InteractionStatus.Logout: return 'logout' as const;
        default: return 'none' as const;
      }
    }),
    startWith('none' as const)
  );
  /** Tries to get initial user info and updates itself after any event. */
  private readonly userInfo$ = merge(
    this.authSvc.authorizedAccount$.pipe(
      startWith(undefined),
      filter(Boolean),
      tap(x => this.userService.curremntUserName.set(x?.name)),
      map(x => {        

        const roles = this.extractRoleValues(x!.idTokenClaims!.roles);        
        const userInfo = this.createUserInfo(x, roles);
        this.userService.userInfo.set(userInfo);        
        return userInfo;
      })
    )
  );

  constructor(private authSvc: AuthService, private msalBroadcastSvc: MsalBroadcastService, private userService: UserService) { }

  login() {
    this.authSvc.login().subscribe();
  }

  logout() {
    this.authSvc.logout().subscribe();
  }
  private createUserInfo(user: AccountInfo | undefined, roles: {features: string[], orgCodes: string[], unknowns: string[]}): UserInfo {
    return {
      hasAccessToken: !!user,
      name: user
        ? user.name ?? user.username ?? 'Logged In'
        : 'Not logged in',
        features: roles.features,
        orgCodes: roles.orgCodes,
        unknowns: roles.unknowns
    };
  }

  extractRoleValues(roles: string[] | undefined) {
    const roleTypes:  {[key: string]: string} = {
      feature: 'feature',
      orgCode: 'orgcode'
    };

    const userFeatures = [] as string[];
    const userOrgCodes = [] as string[];
    const userUnknowns = [] as string[];

    if (roles !== undefined && roles.length) {
      roles.forEach(role => {
        const splitRole = role.split('~~');
        const roleType = splitRole[0].toLowerCase();
        const roleValue = splitRole[1];

        switch(roleType) {
          case roleTypes.feature:
            userFeatures.push(roleValue);
            break;
          case roleTypes.orgCode:
            userOrgCodes.push(roleValue);
            break;
          default:
            userUnknowns.push(roleValue);
        }
      });
    }
    return { features: userFeatures, orgCodes: userOrgCodes, unknowns: userUnknowns };
  }

}
